import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n/LangProvider';

type Opt = { id: string; labelEn: string; labelFr: string };
type Question = { id: string; type: 'text' | 'choice'; promptEn: string; promptFr: string; options: Opt[] };
type Response = { email: string; answers: Record<string, string>; submittedAt: string };

async function handle<T>(res: globalThis.Response): Promise<T> {
  if (!res.ok) { const d = await res.text().catch(() => ''); throw new Error(`API ${res.status}: ${d}`); }
  return res.json() as Promise<T>;
}
const uid = () => Math.random().toString(36).slice(2, 8);

export default function AdminPoll() {
  const { t } = useT();
  const [tab, setTab] = useState<'questions' | 'responses'>('questions');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [responses, setResponses] = useState<Response[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const cfg = await handle<{ questions: Question[] }>(await fetch('/api/poll-config'));
        setQuestions(cfg.questions || []);
        const subs = await handle<{ items: Response[] }>(await fetch('/api/poll-submissions'));
        setResponses(subs.items || []);
      } catch (e) { setError((e as Error).message); }
    })();
  }, []);

  const addQuestion = (type: 'text' | 'choice') =>
    setQuestions([...questions, { id: uid(), type, promptEn: '', promptFr: '', options: type === 'choice' ? [{ id: uid(), labelEn: '', labelFr: '' }] : [] }]);
  const removeQuestion = (id: string) => setQuestions(questions.filter((q) => q.id !== id));
  const updateQ = (id: string, patch: Partial<Question>) => setQuestions(questions.map((q) => q.id === id ? { ...q, ...patch } : q));
  const addOpt = (qid: string) => setQuestions(questions.map((q) => q.id === qid ? { ...q, options: [...q.options, { id: uid(), labelEn: '', labelFr: '' }] } : q));
  const removeOpt = (qid: string, oid: string) => setQuestions(questions.map((q) => q.id === qid ? { ...q, options: q.options.filter((o) => o.id !== oid) } : q));
  const updateOpt = (qid: string, oid: string, patch: Partial<Opt>) => setQuestions(questions.map((q) => q.id === qid ? { ...q, options: q.options.map((o) => o.id === oid ? { ...o, ...patch } : o) } : q));

  const save = async () => {
    setBusy(true); setError(null); setMsg(null);
    try {
      await handle(await fetch('/api/poll-config-save', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ questions }) }));
      setMsg('Saved.');
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const clearResponse = async (email: string) => {
    if (!confirm(`Remove ${email}'s response? They'll be able to submit the poll again.`)) return;
    setBusy(true); setError(null);
    try {
      await handle(await fetch('/api/poll-clear', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) }));
      setResponses(responses.filter((r) => r.email !== email));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const headers = ['Email', 'Submitted', ...questions.map((q) => q.promptEn || q.id)];
    const rows = responses.map((r) => [
      r.email, r.submittedAt,
      ...questions.map((q) => {
        const a = r.answers[q.id] || '';
        if (q.type === 'choice') { const o = q.options.find((x) => x.id === a); return o ? o.labelEn : a; }
        return a;
      })
    ]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'poll-responses.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const FIELD = 'w-full bg-bg-base border border-white/[0.12] rounded-lg px-3 py-2 text-sm text-ink outline-none focus:border-brand-red';

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 pt-36 pb-32">
      <Link to="/admin" className="text-sm text-ink-muted hover:text-ink lowercase">{t('admin.back')}</Link>
      <h1 className="font-display text-5xl md:text-6xl tracking-tight text-ink mt-4 mb-8">Poll setup.</h1>

      <div className="flex gap-3 mb-10">
        {(['questions', 'responses'] as const).map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} className={`px-4 py-2 rounded-full text-sm transition-all ${tab === tb ? 'bg-brand-red text-white' : 'bg-bg-elev text-ink-muted hover:text-ink'}`}>{tb}</button>
        ))}
      </div>

      {tab === 'questions' && (
        <div className="space-y-6">
          {questions.map((q, i) => (
            <div key={q.id} className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-mono text-ink-faint uppercase tracking-wider">Q{i + 1} · {q.type}</span>
                <button onClick={() => removeQuestion(q.id)} className="text-xs text-ink-faint hover:text-brand-red">remove</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <input className={FIELD} placeholder="Prompt (EN)" value={q.promptEn} onChange={(e) => updateQ(q.id, { promptEn: e.target.value })} />
                <input className={FIELD} placeholder="Prompt (FR)" value={q.promptFr} onChange={(e) => updateQ(q.id, { promptFr: e.target.value })} />
              </div>
              {q.type === 'choice' && (
                <div className="mt-4 space-y-2 pl-4 border-l border-white/[0.08]">
                  {q.options.map((o) => (
                    <div key={o.id} className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center">
                      <input className={FIELD} placeholder="Option (EN)" value={o.labelEn} onChange={(e) => updateOpt(q.id, o.id, { labelEn: e.target.value })} />
                      <div className="flex gap-2">
                        <input className={FIELD} placeholder="Option (FR)" value={o.labelFr} onChange={(e) => updateOpt(q.id, o.id, { labelFr: e.target.value })} />
                        <button onClick={() => removeOpt(q.id, o.id)} className="text-xs text-ink-faint hover:text-brand-red px-2">×</button>
                      </div>
                    </div>
                  ))}
                  <button onClick={() => addOpt(q.id)} className="text-xs text-brand-red hover:underline mt-1">+ add option</button>
                </div>
              )}
            </div>
          ))}

          <div className="flex gap-3">
            <button onClick={() => addQuestion('text')} className="text-sm text-ink-muted hover:text-ink border border-white/[0.12] rounded-lg px-4 py-2">+ text question</button>
            <button onClick={() => addQuestion('choice')} className="text-sm text-ink-muted hover:text-ink border border-white/[0.12] rounded-lg px-4 py-2">+ choice question</button>
          </div>

          <button onClick={save} disabled={busy} className="bg-brand-red text-white text-sm rounded-md px-6 py-3 disabled:opacity-40 hover:bg-brand-red-soft transition-colors">
            {busy ? 'Saving…' : 'Save poll'}
          </button>
        </div>
      )}

      {tab === 'responses' && (
        <div>
          <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-xs font-mono text-ink-faint uppercase tracking-wider">{responses.length} responses</p>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search emails…" className="bg-bg-base border border-white/[0.12] rounded-lg px-3 py-1.5 text-sm text-ink outline-none focus:border-brand-red w-56" />
            </div>
            {responses.length > 0 && <button onClick={exportCsv} className="text-sm text-brand-red hover:underline">Export CSV</button>}
          </div>
          {responses.length === 0 ? (
            <p className="text-ink-muted">No responses yet.</p>
          ) : (
            <div className="space-y-3">
              {responses.filter((r) => r.email.toLowerCase().includes(search.toLowerCase())).map((r) => (
                <div key={r.email} className="card p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-ink font-mono">{r.email}</p>
                    <button onClick={() => clearResponse(r.email)} disabled={busy} className="text-xs text-ink-faint hover:text-brand-red disabled:opacity-40 ml-3">Remove</button>
                  </div>
                  <div className="space-y-2">
                    {questions.map((q) => {
                      const a = r.answers[q.id];
                      const display = q.type === 'choice' ? (q.options.find((o) => o.id === a)?.labelEn || a) : a;
                      return <div key={q.id} className="text-sm"><span className="text-ink-faint">{q.promptEn || q.id}: </span><span className="text-ink">{display || '—'}</span></div>;
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {msg && <p className="mt-5 text-emerald-400 text-sm">{msg}</p>}
      {error && <p className="mt-5 text-red-400 text-sm">{error}</p>}
    </div>
  );
}
